import { AnNamElement } from '../../base-element';

export class AnCarousel extends AnNamElement {
  static properties = {
    autoPlay: { type: Boolean, attribute: 'auto-play' },
    autoPlayDuration: { type: Number, attribute: 'auto-play-duration' },
    pauseOnHover: { type: Boolean, attribute: 'pause-on-hover' },
    pauseOnFocus: { type: Boolean, attribute: 'pause-on-focus' },
    displayMode: { type: String, attribute: 'display-mode' },
  };

  declare autoPlay: boolean;
  declare autoPlayDuration: number;
  declare pauseOnHover: boolean;
  declare pauseOnFocus: boolean;
  declare displayMode: string; // "single" | "peek"

  constructor() {
    super();
    this.autoPlay = false;
    this.autoPlayDuration = 5000;
    this.pauseOnHover = true;
    this.pauseOnFocus = true;
    this.displayMode = 'single';
  }

  // Suppress Lit render to preserve server-rendered slide children
  protected override scheduleUpdate(): void {}

  // _virtualIndex: position in the extended track [clone-last, slide1…slideN, clone-first]
  // At rest: 1..N. In flight: 0 (clone-last) or N+1 (clone-first).
  private _virtualIndex = 0;
  private _slides: HTMLElement[] = []; // real slides only (N items)
  private _indicators: HTMLButtonElement[] = [];
  private _counterEl: HTMLElement | null = null;
  private _track: HTMLElement | null = null;
  private _autoPlayTimer: ReturnType<typeof setInterval> | null = null;
  // Single tracked transitionend handler — replaced on each navigation to prevent accumulation
  private _pendingTransitionEnd: ((e: TransitionEvent) => void) | null = null;

  private _isDragging = false;
  private _isTransitioning = false;
  private _dragStartX = 0;
  private _dragDelta = 0;

  // displayedIndex: 0-based index into _slides for UI (indicators, counter, aria)
  private get _displayedIndex(): number {
    const N = this._slides.length;
    return N > 0 ? (this._virtualIndex - 1 + N) % N : 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this._slides = Array.from(this.querySelectorAll<HTMLElement>('.an-carousel__slide'));
    this._track = this.querySelector<HTMLElement>('.an-carousel__track');

    if (this._slides.length === 0) return;

    if (this._slides.length > 1) {
      this._insertClones();
      this._buildControls();
      this._bindEvents();
      this._goTo(1, false); // start on real slide 1 (virtual index 1)
    } else {
      this._goTo(0, false); // single slide — no clones, position at 0
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopAutoPlay();
    this._unbindEvents();
    this._clearTransitionEnd();
  }

  // Insert sentinel clones: [clone-last][slide1…slideN][clone-first]
  private _insertClones(): void {
    if (!this._track) return;
    const N = this._slides.length;

    const cloneLast = this._slides[N - 1].cloneNode(true) as HTMLElement;
    cloneLast.setAttribute('aria-hidden', 'true');
    cloneLast.removeAttribute('id');

    const cloneFirst = this._slides[0].cloneNode(true) as HTMLElement;
    cloneFirst.setAttribute('aria-hidden', 'true');
    cloneFirst.removeAttribute('id');

    this._track.prepend(cloneLast);
    this._track.appendChild(cloneFirst);
  }

  private _buildControls(): void {
    const controls = document.createElement('div');
    controls.className = 'an-carousel__controls';

    const indicatorsWrap = document.createElement('div');
    indicatorsWrap.className = 'an-carousel__indicators';
    indicatorsWrap.setAttribute('role', 'tablist');
    indicatorsWrap.setAttribute('aria-label', 'Slide indicators');

    this._slides.forEach((_, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'an-carousel__indicator';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', `Slide ${i + 1}`);
      btn.setAttribute('aria-selected', 'false');
      btn.addEventListener('click', () => {
        this._goTo(i + 1); // virtual index = display index + 1
        if (this.autoPlay) {
          this._stopAutoPlay();
          this._startAutoPlay();
        }
      });
      this._indicators.push(btn);
      indicatorsWrap.appendChild(btn);
    });

    this._counterEl = document.createElement('span');
    this._counterEl.className = 'an-carousel__counter';
    this._counterEl.setAttribute('aria-hidden', 'true');

    controls.appendChild(indicatorsWrap);
    controls.appendChild(this._counterEl);
    this.appendChild(controls);
  }

  // Peek mode: slides are 80% wide, 10% peek on each side.
  private _slidePercent(): number {
    return this.displayMode === 'peek' ? 80 : 100;
  }

  private _peekOffset(): number {
    return this.displayMode === 'peek' ? 10 : 0;
  }

  private _trackTransform(virtualIndex: number): string {
    const offset = this._peekOffset();
    const slide = this._slidePercent();
    return offset > 0
      ? `translateX(calc(${offset}% - ${virtualIndex * slide}%))`
      : `translateX(-${virtualIndex * slide}%)`;
  }

  private _goTo(virtualIndex: number, animate = true): void {
    this._virtualIndex = virtualIndex;

    if (this._track) {
      this._track.style.transition = animate ? 'transform var(--duration-long, 500ms) ease' : 'none';
      this._track.style.transform = this._trackTransform(virtualIndex);

      if (animate) {
        this._isTransitioning = true;
        this._clearTransitionEnd();

        const handler = (e: TransitionEvent) => {
          if (e.target !== this._track || e.propertyName !== 'transform') return;
          const N = this._slides.length;
          if (this._virtualIndex === 0) {
            this._silentJump(N); // clone-last → real last
          } else if (this._virtualIndex === N + 1) {
            this._silentJump(1); // clone-first → real first
          }
          this._isTransitioning = false;
          this._clearTransitionEnd();
        };

        this._pendingTransitionEnd = handler;
        this._track.addEventListener('transitionend', handler);
      }
    }

    this._updateUI();
  }

  // Instantly reposition to a real slide without animation
  private _silentJump(virtualIndex: number): void {
    this._virtualIndex = virtualIndex;
    if (!this._track) return;
    this._track.style.transition = 'none';
    this._track.style.transform = this._trackTransform(virtualIndex);
  }

  private _clearTransitionEnd(): void {
    if (this._pendingTransitionEnd && this._track) {
      this._track.removeEventListener('transitionend', this._pendingTransitionEnd);
    }
    this._pendingTransitionEnd = null;
  }

  private _updateUI(): void {
    const di = this._displayedIndex;

    this._indicators.forEach((btn, i) => {
      const active = i === di;
      btn.classList.toggle('an-carousel__indicator--active', active);
      btn.setAttribute('aria-selected', String(active));
    });

    if (this._counterEl) {
      const current = String(di + 1).padStart(2, '0');
      const total = String(this._slides.length).padStart(2, '0');
      // Safe: only controlled numeric strings — no user content
      this._counterEl.innerHTML = `<strong class="an-carousel__counter-current">${current}</strong><span class="an-carousel__counter-sep" aria-hidden="true"> / </span>${total}`;
    }

    this._slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', String(i !== di));
    });
  }

  private _next(): void {
    this._goTo(this._virtualIndex + 1); // freely enter clone range; transitionend teleports back
  }

  private _prev(): void {
    this._goTo(this._virtualIndex - 1);
  }

  private _startAutoPlay(): void {
    this._stopAutoPlay();
    this._resumeAutoPlay();
    this._autoPlayTimer = setInterval(() => this._next(), this.autoPlayDuration);
  }

  private _stopAutoPlay(): void {
    if (this._autoPlayTimer !== null) {
      clearInterval(this._autoPlayTimer);
      this._autoPlayTimer = null;
    }
  }

  private _resumeAutoPlay(): void {}

  private _onPointerDown = (e: PointerEvent): void => {
    if (this._isTransitioning) return e.preventDefault();
    if ((e.target as HTMLElement).closest('.an-carousel__indicator')) return;
    this._isDragging = true;
    this._dragStartX = e.clientX;
    this._dragDelta = 0;
    this.setPointerCapture(e.pointerId);
    if (this.autoPlay) this._stopAutoPlay();
  };

  private _onPointerMove = (e: PointerEvent): void => {
    if (!this._isDragging || !this._track) return;
    this._dragDelta = e.clientX - this._dragStartX;
    this._track.style.transition = 'none';
    const offset = this._peekOffset();
    const slide = this._slidePercent();
    this._track.style.transform =
      offset > 0
        ? `translateX(calc(${offset}% - ${this._virtualIndex * slide}% + ${this._dragDelta}px))`
        : `translateX(calc(-${this._virtualIndex * slide}% + ${this._dragDelta}px))`;
  };

  private _onPointerUp = (_e: PointerEvent): void => {
    if (!this._isDragging) return;
    this._isDragging = false;
    const threshold = this.offsetWidth * 0.2;
    if (this._dragDelta < -threshold) {
      this._next();
    } else if (this._dragDelta > threshold) {
      this._prev();
    } else {
      this._goTo(this._virtualIndex); // snap back
    }
    if (this.autoPlay) this._startAutoPlay();
  };

  private _onMouseEnter = (): void => {
    if (this.pauseOnHover && this.autoPlay) this._stopAutoPlay();
  };

  private _onMouseLeave = (): void => {
    if (this.pauseOnHover && this.autoPlay) this._startAutoPlay();
  };

  private _onFocusIn = (): void => {
    if (this.pauseOnFocus && this.autoPlay) this._stopAutoPlay();
  };

  private _onFocusOut = (e: FocusEvent): void => {
    if (!this.contains(e.relatedTarget as Node)) {
      if (this.pauseOnFocus && this.autoPlay) this._startAutoPlay();
    }
  };

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._prev();
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._next();
    }
  };

  private _onDragStart = (e: DragEvent): void => {
    e.preventDefault(); // stop native image drag hijacking pointer capture
  };

  private _onDocumentVisible = (): void => {
    if (document.visibilityState === 'hidden') {
      this._stopAutoPlay();
    } else {
      this._startAutoPlay();
    }
  };

  private _bindEvents(): void {
    this.addEventListener('dragstart', this._onDragStart);
    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerup', this._onPointerUp);
    this.addEventListener('pointercancel', this._onPointerUp);
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);
    this.addEventListener('focusin', this._onFocusIn);
    this.addEventListener('focusout', this._onFocusOut);
    this.addEventListener('keydown', this._onKeyDown);

    if (this.autoPlay) {
      document.addEventListener('visibilitychange', this._onDocumentVisible);
    }
  }

  private _unbindEvents(): void {
    this.removeEventListener('dragstart', this._onDragStart);
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerup', this._onPointerUp);
    this.removeEventListener('pointercancel', this._onPointerUp);
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    this.removeEventListener('focusin', this._onFocusIn);
    this.removeEventListener('focusout', this._onFocusOut);
    this.removeEventListener('keydown', this._onKeyDown);

    if (this.autoPlay) {
      document.removeEventListener('visibilitychange', this._onDocumentVisible);
    }
  }
}

if (!customElements.get('an-carousel')) {
  customElements.define('an-carousel', AnCarousel);
}
